// ANGEL FLIGHT — seeded deterministic RNG (mulberry32).
//
// One small, fast, 32-bit PRNG. Same seed ⇒ same sequence forever, so spread,
// AI dodges, and daily-seed runs are reproducible and unit-testable. Pure.

/** The RNG handle. All draws are deterministic for a fixed seed. */
export interface Rng {
  /** Next float in [0, 1). */
  next(): number;
  /** Integer in [min, max] inclusive on both ends. */
  int(min: number, max: number): number;
  /** Float in [min, max). */
  range(min: number, max: number): number;
  /** A random element of `arr`, or undefined if empty. */
  pick<T>(arr: readonly T[]): T | undefined;
  /** True with probability `p` (clamped to [0,1]). */
  chance(p: number): boolean;
}

/**
 * mulberry32 — a single-32-bit-word generator. Cheap, decent distribution, and
 * fully deterministic. `seed` is coerced to a uint32.
 */
export function createRng(seed: number): Rng {
  let a = seed >>> 0;

  const next = (): number => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  const range = (min: number, max: number): number => min + next() * (max - min);

  const int = (min: number, max: number): number =>
    Math.floor(min + next() * (max - min + 1));

  const pick = <T>(arr: readonly T[]): T | undefined => {
    if (arr.length === 0) return undefined;
    return arr[Math.floor(next() * arr.length)];
  };

  const chance = (p: number): boolean => next() < (p < 0 ? 0 : p > 1 ? 1 : p);

  return { next, int, range, pick, chance };
}
